/** Maintained single-player bump/stun/kick arcade loop. The simulation owns no
 * clock, renderer, input polling or randomness. Positions use the shared
 * feet-centred platformer convention and every timer is an integer 30 Hz tick. */
import { createPlatformerMotionV2, type PlatformerBodyV1, type PlatformerMotionConfigV1, type PlatformerSurfaceV2 } from './motionBody2d.js';
import { advanceVitals, createVitals, damageVitals, type VitalsState } from './vitals.js';

export type BumpArcadeCommandV1 = { type: 'bump-arcade'; dx: -1 | 0 | 1; jump: boolean; kick: boolean };
export interface BumpArcadePipeV1 { id: string; x: number; y: number }
export interface BumpArcadeEnemyConfigV1 { id: string; pipeId: string; x: number; direction: -1 | 1; speed: number }
export interface BumpArcadeWaveV1 { id: string; enemies: BumpArcadeEnemyConfigV1[] }
export interface BumpArcadeConfigV1 {
  profile: 'bump-arcade@1'; variant: 'standard' | 'alternate'; physics: PlatformerMotionConfigV1;
  arena: { height: number; floorY: number; solids: PlatformerSurfaceV2[]; platformIds: string[];
    start: { x: number; y: number }; pipes: BumpArcadePipeV1[];
    pow: { id: string; x: number; y: number; w: number; h: number; uses: number } };
  waves: BumpArcadeWaveV1[];
  rules: { lives: number; invulnerabilityTicks: number; respawnTicks: number; hitFlashTicks: number;
    timeLimitTicks: number; stunTicks: number; waveDelayTicks: number; bumpReach: number; kickReach: number;
    bumpScore: number; kickScore: number; waveScore: number };
}
export interface BumpArcadeEnemyV1 {
  id: string; pipeId: string; x: number; y: number; direction: -1 | 1;
  state: 'walking' | 'stunned'; stunLeft: number;
}
export interface BumpArcadeSnapshotV1 {
  tick: number; variant: 'standard' | 'alternate'; score: number; phase: 'active' | 'won' | 'lost'; lossCause: null | 'lives' | 'time';
  player: PlatformerBodyV1 & { lives: number; invulnerable: boolean; hitStunned: boolean };
  solids: PlatformerSurfaceV2[]; pipes: BumpArcadePipeV1[];
  pow: { id: string; x: number; y: number; w: number; h: number; uses: number; maxUses: number };
  enemies: BumpArcadeEnemyV1[]; removedIds: string[]; wave: number; totalWaves: number;
  hitsTaken: number; timeLeft: number;
}
export type BumpArcadeEventV1 = { kind: 'jump' | 'land' | 'bump' | 'pow' | 'stun' | 'kick' | 'removed'
  | 'hurt' | 'respawn' | 'wave-clear' | 'pipe-return' | 'wave' | 'won' | 'lost'; tick: number; data: Record<string, unknown> };

const PHYSICS: PlatformerMotionConfigV1 = { dt: 1 / 30, runSpeed: 6, gravity: 42, jumpSpeed: 18,
  maxFallSpeed: 24, jumpCut: 0.45, coyoteTicks: 4, width: 0.9, height: 1.4, worldWidth: 20 };

/** Exact maintained data variants. Callers receive a fresh mutable value;
 * validation and controller construction detach their own copies. */
export function bumpArcadeConfigV1(variant: 'standard' | 'alternate' = 'standard'): BumpArcadeConfigV1 {
  if (variant !== 'standard' && variant !== 'alternate') throw new RangeError('Unsupported bump-arcade variant');
  const alternate = variant === 'alternate';
  return {
    profile: 'bump-arcade@1', variant, physics: { ...PHYSICS },
    arena: { height: 11, floorY: 10,
      solids: [
        { id: 'floor', mode: 'solid', x: 0, y: 10, w: 20, h: 1 },
        { id: 'upper', mode: 'oneWayTop', x: 1.5, y: alternate ? 6.1 : 6.4, w: 17, h: 0.35 },
        { id: 'pow', mode: 'solid', x: alternate ? 4.2 : 9.2, y: 7.6, w: 1.6, h: 0.6 },
      ], platformIds: ['upper'], start: { x: alternate ? 15.5 : 4, y: 10 },
      pipes: [{ id: 'pipe-left', x: 1.8, y: alternate ? 6.1 : 6.4 }, { id: 'pipe-right', x: 18.2, y: alternate ? 6.1 : 6.4 }],
      pow: { id: 'pow', x: alternate ? 4.2 : 9.2, y: 7.6, w: 1.6, h: 0.6, uses: alternate ? 2 : 1 } },
    waves: alternate ? [
      { id: 'wave-a', enemies: [{ id: 'crab-a', pipeId: 'pipe-right', x: 15.5, direction: -1, speed: 0.45 }] },
      { id: 'wave-b', enemies: [
        { id: 'crab-b', pipeId: 'pipe-left', x: 5.5, direction: 1, speed: 0.5 },
        { id: 'crab-c', pipeId: 'pipe-right', x: 10, direction: -1, speed: 0.55 },
        { id: 'crab-d', pipeId: 'pipe-right', x: 14.5, direction: -1, speed: 0.5 },
      ] },
    ] : [
      { id: 'wave-1', enemies: [{ id: 'shell-1', pipeId: 'pipe-left', x: 4, direction: 1, speed: 0.4 }] },
      { id: 'wave-2', enemies: [
        { id: 'shell-2', pipeId: 'pipe-left', x: 7, direction: 1, speed: 0.45 },
        { id: 'shell-3', pipeId: 'pipe-right', x: 13, direction: -1, speed: 0.45 },
      ] },
    ],
    rules: { lives: 3, invulnerabilityTicks: 45, respawnTicks: 12, hitFlashTicks: 10,
      timeLimitTicks: alternate ? 2100 : 1800, stunTicks: 300, waveDelayTicks: 15,
      bumpReach: 1.2, kickReach: 1.15, bumpScore: 25, kickScore: 100, waveScore: 250 },
  };
}

function record(value: unknown, keys: readonly string[], label = 'record'): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) throw new RangeError(`Invalid bump-arcade ${label}`);
}
function finite(value: unknown, min: number, max: number, integer = false): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max || integer && !Number.isSafeInteger(value)) {
    throw new RangeError('Invalid bump-arcade numeric bound');
  }
}
const ID = /^[a-z][a-z0-9-]{0,39}$/;
export function validateBumpArcadeConfigV1(input: unknown): BumpArcadeConfigV1 {
  record(input, ['profile','variant','physics','arena','waves','rules']);
  if (input.profile !== 'bump-arcade@1' || !['standard','alternate'].includes(String(input.variant))) throw new RangeError('Unsupported bump-arcade profile or variant');
  record(input.physics, ['dt','runSpeed','gravity','jumpSpeed','maxFallSpeed','jumpCut','coyoteTicks','width','height','worldWidth'], 'physics');
  const physics = input.physics;
  for (const key of Object.keys(physics)) finite(physics[key], 0, 120, key === 'coyoteTicks');
  if (physics.dt !== 1 / 30) throw new RangeError('Bump arcade requires fixed 30Hz');
  record(input.arena, ['height','floorY','solids','platformIds','start','pipes','pow'], 'arena');
  const arena = input.arena;
  finite(arena.height, 4, 32); finite(arena.floorY, 2, Number(arena.height));
  record(arena.start, ['x','y'], 'start'); finite(arena.start.x, Number(physics.width) / 2, Number(physics.worldWidth) - Number(physics.width) / 2); finite(arena.start.y, 0, Number(arena.height));
  const ids = new Set<string>();
  if (!Array.isArray(arena.solids) || arena.solids.length < 3 || arena.solids.length > 16) throw new RangeError('Invalid bump-arcade surfaces');
  const solids = arena.solids as Record<string, unknown>[];
  for (const solid of solids) {
    record(solid, ['id','mode','x','y','w','h'], 'surface');
    if (typeof solid.id !== 'string' || !ID.test(solid.id) || ids.has(solid.id) || !['solid','oneWayTop'].includes(String(solid.mode))) throw new RangeError('Invalid bump-arcade surface');
    ids.add(solid.id); finite(solid.x, 0, Number(physics.worldWidth)); finite(solid.y, 0, Number(arena.height));
    finite(solid.w, 0.01, Number(physics.worldWidth) - Number(solid.x)); finite(solid.h, 0.01, Number(arena.height) - Number(solid.y));
  }
  if (!Array.isArray(arena.platformIds) || arena.platformIds.length < 1 || arena.platformIds.length > 8
    || new Set(arena.platformIds).size !== arena.platformIds.length
    || arena.platformIds.some((id: unknown) => typeof id !== 'string' || !solids.some(s => s.id === id && s.mode === 'oneWayTop'))) throw new RangeError('Invalid bump platforms');
  if (!Array.isArray(arena.pipes) || arena.pipes.length !== 2) throw new RangeError('Bump arcade requires two pipes');
  const pipeIds = new Set<string>();
  for (const pipe of arena.pipes) {
    record(pipe, ['id','x','y'], 'pipe');
    if (typeof pipe.id !== 'string' || !ID.test(pipe.id) || ids.has(pipe.id) || pipeIds.has(pipe.id)) throw new RangeError('Invalid bump pipe');
    pipeIds.add(pipe.id); ids.add(pipe.id); finite(pipe.x, 0, Number(physics.worldWidth)); finite(pipe.y, 0, Number(arena.height));
  }
  record(arena.pow, ['id','x','y','w','h','uses'], 'POW');
  const pow = arena.pow as Record<string, unknown>;
  if (pow.id !== 'pow' || !ids.has('pow')) throw new RangeError('Required POW surface is missing');
  for (const key of ['x','y','w','h'] as const) finite(pow[key], 0.01, key === 'x' || key === 'w' ? Number(physics.worldWidth) : Number(arena.height));
  finite(pow.uses, 1, 4, true);
  const powSurface = solids.find(s => s.id === 'pow')!;
  if (powSurface.mode !== 'solid' || ['x','y','w','h'].some(key => powSurface[key] !== pow[key])) throw new RangeError('POW geometry must match its solid');
  if (!Array.isArray(input.waves) || input.waves.length < 2 || input.waves.length > 4) throw new RangeError('Invalid bump-arcade wave count');
  const waveIds = new Set<string>(); let enemyCount = 0;
  for (const wave of input.waves) {
    record(wave, ['id','enemies'], 'wave');
    if (typeof wave.id !== 'string' || !ID.test(wave.id) || ids.has(wave.id) || waveIds.has(wave.id)) throw new RangeError('Invalid bump wave');
    waveIds.add(wave.id); ids.add(wave.id);
    if (!Array.isArray(wave.enemies) || wave.enemies.length < 1 || wave.enemies.length > 4) throw new RangeError('Invalid bump enemy count');
    for (const enemy of wave.enemies) {
      record(enemy, ['id','pipeId','x','direction','speed'], 'enemy');
      if (typeof enemy.id !== 'string' || !ID.test(enemy.id) || ids.has(enemy.id) || !pipeIds.has(String(enemy.pipeId)) || ![-1,1].includes(enemy.direction as number)) throw new RangeError('Invalid bump enemy');
      ids.add(enemy.id); enemyCount++; finite(enemy.x, 1.5, Number(physics.worldWidth) - 1.5); finite(enemy.speed, 0.05, 3);
    }
  }
  if (enemyCount > 12) throw new RangeError('Too many bump enemies');
  record(input.rules, ['lives','invulnerabilityTicks','respawnTicks','hitFlashTicks','timeLimitTicks','stunTicks','waveDelayTicks','bumpReach','kickReach','bumpScore','kickScore','waveScore'], 'rules');
  for (const [key,value] of Object.entries(input.rules)) finite(value, key === 'lives' || key === 'timeLimitTicks' || key === 'stunTicks' ? 1 : 0,
    key === 'timeLimitTicks' ? 3600 : 1000, !['bumpReach','kickReach'].includes(key));
  if (Number(input.rules.hitFlashTicks) > Number(input.rules.invulnerabilityTicks) || Number(input.rules.respawnTicks) > Number(input.rules.invulnerabilityTicks)) throw new RangeError('Invalid bump recovery window');
  const out = structuredClone(input) as unknown as BumpArcadeConfigV1;
  createPlatformerMotionV2(out.physics);
  for (const key of ['solids','pipes'] as const) out.arena[key].sort((a,b) => a.id.localeCompare(b.id));
  out.arena.platformIds.sort(); out.waves.forEach(w => w.enemies.sort((a,b) => a.id.localeCompare(b.id)));
  return out;
}

export function createBumpArcadeV1(input: BumpArcadeConfigV1) {
  const config = validateBumpArcadeConfigV1(input); const { arena, physics, rules } = config;
  const move = createPlatformerMotionV2(physics);
  const vitalsConfig = { maxHealth: 1, lives: rules.lives, invulnerabilityTicks: rules.invulnerabilityTicks,
    respawnTicks: rules.respawnTicks, downPolicy: 'respawn' as const };
  const initialBody = (): PlatformerBodyV1 => ({ ...arena.start, vx: 0, vy: 0, facing: 1, grounded: true, airborne: 0, jumpHeld: false });
  const upper = arena.solids.filter(s => arena.platformIds.includes(s.id)).sort((a,b) => a.id.localeCompare(b.id));
  const enemyY = upper[0]!.y;
  const spawnWave = (index: number): BumpArcadeEnemyV1[] => config.waves[index]!.enemies.map(e => ({ ...e, y: enemyY, state: 'walking', stunLeft: 0 }));
  let tick = 0, score = 0, hitsTaken = 0, waveIndex = 0, powUses = arena.pow.uses, waveDelay = 0;
  let phase: BumpArcadeSnapshotV1['phase'] = 'active'; let lossCause: BumpArcadeSnapshotV1['lossCause'] = null;
  let body = initialBody(); let vitals: VitalsState = createVitals(vitalsConfig); let enemies = spawnWave(0);
  let pending: BumpArcadeCommandV1 | null = null; let events: BumpArcadeEventV1[] = []; const removed = new Set<string>();
  const emit = (kind: BumpArcadeEventV1['kind'], data: Record<string, unknown> = {}) => { if (events.length < 32) events.push({ kind, tick, data }); };
  const lose = (cause: 'lives'|'time') => { phase='lost'; lossCause=cause; emit('lost',{cause,score}); };
  const stun = (enemy: BumpArcadeEnemyV1, source: 'bump'|'pow') => {
    if (enemy.state !== 'walking') return;
    enemy.state='stunned'; enemy.stunLeft=rules.stunTicks; score+=rules.bumpScore; emit('stun',{id:enemy.id,source,x:enemy.x,y:enemy.y});
  };
  return {
    applyCommand(slot: number, command: unknown): boolean {
      if (slot !== 0 || phase !== 'active') return false;
      try { record(command, ['type','dx','jump','kick'], 'command'); } catch { return false; }
      if (command.type !== 'bump-arcade' || ![-1,0,1].includes(command.dx as number) || typeof command.jump !== 'boolean' || typeof command.kick !== 'boolean') return false;
      pending={type:'bump-arcade',dx:command.dx as -1|0|1,jump:command.jump,kick:command.kick}; return true;
    },
    step(): void {
      tick++; const command=pending ?? {type:'bump-arcade' as const,dx:0 as const,jump:false,kick:false}; pending=null;
      if (phase !== 'active') return;
      const advanced=advanceVitals(vitals,vitalsConfig,tick); vitals=advanced.state;
      if (advanced.respawned) { body=initialBody(); emit('respawn',{lives:vitals.lives}); }
      if (vitals.phase !== 'respawning') {
        const before={...body}; const result=move(body,command,arena.solids); body=result.body;
        if (result.jumped) emit('jump'); if (result.landed) emit('land');
        const beforeHead=before.y-physics.height, afterHead=body.y-physics.height;
        for (const platform of upper) if (before.vy < 0 && beforeHead >= platform.y+platform.h && afterHead <= platform.y+platform.h
          && body.x+physics.width/2 > platform.x && body.x-physics.width/2 < platform.x+platform.w) {
          const targets=enemies.filter(e=>e.state==='walking' && Math.abs(e.x-body.x)<=rules.bumpReach).sort((a,b)=>a.id.localeCompare(b.id));
          emit('bump',{platformId:platform.id,x:body.x,count:targets.length}); for(const enemy of targets)stun(enemy,'bump');
        }
        if (result.contacts.some(c=>c.solidId===arena.pow.id && c.side==='bottom') && powUses>0) {
          powUses--; emit('pow',{id:arena.pow.id,uses:powUses}); for(const enemy of [...enemies].sort((a,b)=>a.id.localeCompare(b.id)))stun(enemy,'pow');
        }
        if (command.kick) {
          const target=enemies.filter(e=>e.state==='stunned' && Math.abs(e.x-body.x)<=rules.kickReach && Math.abs(e.y-body.y)<=0.8)
            .sort((a,b)=>a.id.localeCompare(b.id))[0];
          if (target) { enemies=enemies.filter(e=>e.id!==target.id); removed.add(target.id); score+=rules.kickScore;
            emit('kick',{id:target.id,x:target.x,y:target.y}); emit('removed',{id:target.id}); }
        }
      }
      for (const enemy of enemies) {
        if (enemy.state==='stunned') {
          enemy.stunLeft--; if (enemy.stunLeft<=0) { enemy.state='walking'; enemy.stunLeft=0; enemy.y=arena.floorY; }
        } else {
          enemy.x+=enemy.direction*config.waves[waveIndex]!.enemies.find(e=>e.id===enemy.id)!.speed*physics.dt;
          if (enemy.x<=1.8 || enemy.x>=physics.worldWidth-1.8) { enemy.x=Math.max(1.8,Math.min(physics.worldWidth-1.8,enemy.x)); enemy.direction=enemy.x<=1.8?1:-1; }
        }
      }
      if (vitals.phase==='alive') {
        const contact=enemies.filter(e=>e.state==='walking' && Math.abs(e.x-body.x)<=0.75 && Math.abs(e.y-body.y)<=0.8).sort((a,b)=>a.id.localeCompare(b.id))[0];
        if(contact){const damaged=damageVitals(vitals,vitalsConfig,1,tick);vitals=damaged.state;if(damaged.applied){hitsTaken++;emit('hurt',{id:contact.id,lives:vitals.lives});if(damaged.downed)body=initialBody();if(vitals.phase==='eliminated')lose('lives');}}
      }
      if (phase!=='active') return;
      if(enemies.length===0){
        if(waveIndex===config.waves.length-1){score+=rules.waveScore;phase='won';emit('wave-clear',{wave:waveIndex+1});emit('won',{score});return;}
        if(waveDelay===0){score+=rules.waveScore;waveDelay=rules.waveDelayTicks;emit('wave-clear',{wave:waveIndex+1});body=initialBody();}
        waveDelay--;
        if(waveDelay===0){waveIndex++;enemies=spawnWave(waveIndex);for(const enemy of enemies)emit('pipe-return',{id:enemy.id,pipeId:enemy.pipeId});emit('wave',{wave:waveIndex+1});}
      }
      if(tick>=rules.timeLimitTicks&&phase==='active')lose('time');
    },
    snapshot(): BumpArcadeSnapshotV1 {
      return structuredClone({tick,variant:config.variant,score,phase,lossCause,player:{...body,lives:vitals.lives,invulnerable:vitals.phase!=='alive'||tick<vitals.invulnerableUntil,
        hitStunned:vitals.phase==='respawning'||tick<vitals.invulnerableUntil-rules.invulnerabilityTicks+rules.hitFlashTicks},
        solids:arena.solids,pipes:arena.pipes,pow:{...arena.pow,uses:powUses,maxUses:arena.pow.uses},enemies,removedIds:[...removed].sort(),
        wave:waveIndex+1,totalWaves:config.waves.length,hitsTaken,timeLeft:Math.max(0,rules.timeLimitTicks-tick)});
    },
    drainEvents(): BumpArcadeEventV1[]{const out=events;events=[];return out;},
  };
}
